import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Settings, AlertTriangle, X, Check } from "lucide-react";
import { fetchSystemSettings, updateSystemSetting } from "../../redux/actionCreator";
import "./systemSettingsPage.scss";

/**
 * Global security feature kill-switches.
 *
 * Each row maps to a single backend toggle (constants/systemSettings.ts).
 * High-risk switches require typing the feature name before flipping OFF;
 * medium-risk shows a yes/no confirm; low-risk flips immediately.
 *
 * super_admin only — the route is gated server-side via `permissions.manage`.
 * Toggling a switch persists immediately and invalidates the backend's
 * settings cache (~5s TTL) so the effect propagates within one request.
 */
export default function SystemSettingsPage() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [groups, setGroups] = useState([]);
  const [values, setValues] = useState({});
  const [defaults, setDefaults] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const [confirm, setConfirm] = useState(null); // { item, nextVal } | null
  const [confirmText, setConfirmText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const res = await dispatch(fetchSystemSettings());
    setLoading(false);
    if (!res?.status) {
      setErr(res?.message || "Failed to load settings");
      return;
    }
    setGroups(res.data?.groups || []);
    setValues(res.data?.values || {});
    setDefaults(res.data?.defaults || {});
  }, [dispatch]);

  useEffect(() => { load(); }, [load]);

  // Decide whether a flip needs confirmation. Always confirm OFF for
  // medium/high — turning ON is harmless (restores the safer default).
  const needsConfirm = (item, nextVal) => {
    if (nextVal) return false; // turning ON is safe
    return item.risk === "medium" || item.risk === "high";
  };

  const apply = async (item, nextVal) => {
    setBusyKey(item.key);
    const res = await dispatch(updateSystemSetting(item.key, nextVal));
    setBusyKey(null);
    if (!res?.status) {
      alert(res?.message || "Failed to update");
      return;
    }
    setValues((cur) => ({ ...cur, [item.key]: nextVal }));
  };

  const onToggle = (item, e) => {
    const nextVal = e.target.checked;
    if (!needsConfirm(item, nextVal)) {
      apply(item, nextVal);
      return;
    }
    setConfirm({ item, nextVal });
    setConfirmText("");
  };

  const confirmRequired = () => confirm?.item?.risk === "high" ? confirm.item.label : null;
  const confirmOk = () => {
    const req = confirmRequired();
    if (!req) return true;
    return confirmText.trim().toLowerCase() === req.toLowerCase();
  };

  const onConfirm = async () => {
    if (!confirm) return;
    if (!confirmOk()) return;
    const c = confirm;
    setConfirm(null);
    setConfirmText("");
    await apply(c.item, c.nextVal);
  };

  if (loading) {
    return <div className="systemsettings-page"><div className="text-center py-5"><Spinner animation="border" /></div></div>;
  }
  if (err) {
    return (
      <div className="systemsettings-page">
        <div className="alert alert-danger m-3">
          <strong>{err}</strong>
          <div className="small mt-1">Requires <code>permissions.manage</code> (super_admin by default).</div>
        </div>
      </div>
    );
  }

  return (
    <div className="systemsettings-page">
      <div className="ss-head">
        <div>
          <h4 className="m-0 d-inline-flex align-items-center gap-2">
            <Settings size={20} /> System settings
          </h4>
          <small className="text-muted">
            Global on/off switches for security features. Defaults are safer.
            Turning off any high-risk switch requires typing the feature name.
          </small>
        </div>
        <Button size="sm" variant="light" onClick={load}>↻</Button>
      </div>

      {groups.map((g) => (
        <div key={g.title} className="ss-group">
          <h6 className="ss-group-title">{g.title}</h6>
          {g.items.map((item) => {
            const isOn = values[item.key] !== undefined ? values[item.key] : (defaults[item.key] ?? true);
            const isDefault = defaults[item.key] === isOn;
            return (
              <div key={item.key} className={`ss-row risk-${item.risk}`}>
                <div className="ss-row-text">
                  <div className="ss-row-label">
                    {item.label}
                    <span className={`ss-risk badge ms-2 bg-${item.risk === "high" ? "danger" : item.risk === "medium" ? "warning text-dark" : "secondary"}`}>
                      {item.risk}
                    </span>
                    {!isDefault && (
                      <span className="badge bg-info ms-2" title="Differs from default">
                        non-default
                      </span>
                    )}
                  </div>
                  <div className="ss-row-desc">{item.description}</div>
                  {!isOn && item.off_warning && (
                    <div className="ss-row-warning small d-inline-flex align-items-center gap-1 mt-1">
                      <AlertTriangle size={12} /> {item.off_warning}
                    </div>
                  )}
                </div>
                <div className="ss-row-toggle">
                  {busyKey === item.key ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    <Form.Check
                      type="switch"
                      id={`ss-${item.key}`}
                      checked={!!isOn}
                      onChange={(e) => onToggle(item, e)}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      <Modal show={!!confirm} onHide={() => setConfirm(null)} centered>
        {confirm && (
          <>
            <Modal.Header closeButton>
              <Modal.Title className="d-inline-flex align-items-center gap-2">
                <AlertTriangle size={18} className="text-warning" /> Turn off "{confirm.item.label}"?
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="alert alert-warning small mb-3">
                {confirm.item.off_warning}
              </div>
              <div className="small text-muted mb-3">
                Re-enable from this same page when the situation is resolved. The
                backend cache TTL is ~5 seconds; the change propagates almost
                instantly.
              </div>
              {confirmRequired() && (
                <div>
                  <Form.Label className="small">
                    Type <strong>{confirmRequired()}</strong> to confirm:
                  </Form.Label>
                  <Form.Control
                    autoFocus
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={confirmRequired()}
                  />
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button size="sm" variant="light" onClick={() => setConfirm(null)}>
                <X size={14} className="me-1" /> Cancel
              </Button>
              <Button
                size="sm" variant="danger"
                disabled={!confirmOk()}
                onClick={onConfirm}
              >
                <Check size={14} className="me-1" /> Turn OFF
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      <div className="text-muted small mt-3">
        Every toggle has a server-side default (ON for everything). The backend
        falls back to the default for any key never written. Audit who flipped
        what via the existing <code>updateBy</code> records on the SystemSettings document.
      </div>
    </div>
  );
}
